import { RuleGroup, RuleGroupShort } from "@/types/rule.types.ts";

export enum PrincipalType {
  employee = "employee",
  api_user = "api_user",
}

export interface ApiRoleShort {
  id: number;
  title: string;
  description: string;
  employees_count: number;
  is_recalculating: boolean;
  type: RoleType;
  principal_type: PrincipalType;
}

export interface ApiRole extends ApiRoleShort {
  api_permissions: ApiPermissions;
  assign_to: RuleGroup[];
  apply_to: RuleGroup[];
  employee_field_permissions: EmployeeFieldPermissions;
  employee_custom_field_permissions: EmployeeCustomFieldPermissions[];
  employee_custom_table_permissions: EmployeeCustomTablePermissions[];
  employee_folder_permissions: EmployeeFolderPermission[];
  employee_workflow_permissions: EmployeeWorkflowPermission[];
}

export interface ApiRoleSavePayload {
  title: string;
  description: string;
  principal_type?: PrincipalType;
  api_permissions: ApiPermissions;
  assign_to: RuleGroupShort[];
  apply_to: RuleGroupShort[];
  employee_field_permissions: EmployeeFieldPermissions;
  employee_custom_field_permissions: Record<string, PermissionType>;
  employee_custom_table_permissions: ApiRoleCustomTableSavePayload[];
  employee_folder_permissions: Record<number, PermissionType>;
  employee_workflow_permissions: Record<
    number,
    { run: PermissionType; executions: PermissionType }
  >;
}

export interface ApiRoleCustomTableSavePayload {
  table_id: number;
  manage_table: PermissionType;
  history: PermissionType;
  effective_from: PermissionType;
  changed_by: PermissionType;
  field_permissions: Record<string, PermissionType>;
}

export enum PermissionType {
  none = "none",
  view = "view",
  edit = "edit",
}

export enum ApiPermission {
  add_employees = "add_employees",
  dismiss_employees = "dismiss_employees",
  delete_employees = "delete_employees",
  ban_employees = "ban_employees",
  impersonate_employees = "impersonate_employees",
  see_notes = "see_notes",
  see_employee_directory = "see_employee_directory",
  employees_search = "employees_search",
  news_manage_channels = "news_manage_channels",
  news_manage_posts = "news_manage_posts",
  news_delete_all_comments = "news_delete_all_comments",

  recruiting_module = "recruiting_module",
  show_all_jobs = "show_all_jobs",
  manage_jobs = "manage_jobs",
  show_jobs_salaries = "show_jobs_salaries",
  show_all_candidates = "show_all_candidates",
  manage_candidates = "manage_candidates",
  delete_candidates = "delete_candidates",
  show_candidates_salaries = "show_candidates_salaries",
  see_original_resume = "see_original_resume",
  delete_all_comments = "delete_all_comments",
  reporting_recruiting_stage_flow = "reporting_recruiting_stage_flow",
  reporting_recruiting_source_efficiency = "reporting_recruiting_source_efficiency",
  reporting_recruiting_closing_time = "reporting_recruiting_closing_time",
  reporting_recruiting_disqualification_reasons = "reporting_recruiting_disqualification_reasons",
  reporting_employee_headcount = "reporting_employee_headcount",
  reporting_employee_salary = "reporting_employee_salary",
  reporting_employee_total_cost = "reporting_employee_total_cost",
  reporting_export = "reporting_export",
  manage_offers = "manage_offers",
  show_offer_salary = "show_offer_salary",
  ats_manage_scorecards = "ats_manage_scorecards",
  manage_career_sites = "manage_career_sites",
  manage_custom_fields = "manage_custom_fields",

  settings_departments = "settings_departments",
  settings_divisions = "settings_divisions",
  settings_positions_levels = "settings_positions_levels",
  settings_roles_permissions = "settings_roles_permissions",
  settings_organization = "settings_organization",
  settings_integrations = "settings_integrations",
  settings_templates = "settings_templates",
  settings_pipelines = "settings_pipelines",
  settings_sources = "settings_sources",
  settings_candidate_disqualification_reasons = "settings_candidate_disqualification_reasons",
  settings_job_archiving_reasons = "settings_job_archiving_reasons",
  settings_locations = "settings_locations",
  settings_legal_entities = "settings_legal_entities",
  settings_data_protection = "settings_data_protection",
  settings_employment_types = "settings_employment_types",
  settings_employment_contracts = "settings_employment_contracts",
  settings_compensation_change_reasons = "settings_compensation_change_reasons",
  settings_compensation_types = "settings_compensation_types",
  settings_probation_policies = "settings_probation_policies",
  settings_hiring_flows = "settings_hiring_flows",
  settings_work_patterns = "settings_work_patterns",
  settings_security = "settings_security",
  audit_log_read = "audit_log_read",
  settings_organization_links = "settings_organization_links",
  settings_recognitions = "settings_recognitions",
  recognitions_mass_gift = "recognitions_mass_gift",
  settings_document_folders = "settings_document_folders",
  settings_api_users = "settings_api_users",
  settings_webhooks = "settings_webhooks",
  data_import = "data_import",
  manage_holidays = "manage_holidays",

  manage_leave_policies = "manage_leave_policies",
  manage_approval_policy = "manage_approval_policy",
  approve_and_reject_leaves = "approve_and_reject_leaves",
  manage_hiring_plan = "manage_hiring_plan",
  manage_teams = "manage_teams",

  reporting_leave_balance = "reporting_leave_balance",
  reporting_leave_used = "reporting_leave_used",
  reporting_employee_retention = "reporting_employee_retention",
  reporting_people_analytics = "reporting_people_analytics",

  surveys_module = "surveys_module",
  surveys_create = "surveys_create",
  surveys_manage_all = "surveys_manage_all",
  survey_templates = "survey_templates",

  performance_reviews_module = "performance_reviews_module",
  performance_reviews_create = "performance_reviews_create",
  performance_reviews_manage_all = "performance_reviews_manage_all",
  performance_review_templates = "performance_review_templates",

  form_scales = "form_scales",

  workflow_module = "workflow_module",
  workflows_create = "workflows_create",
  workflows_executions = "workflows_executions",
  workflows_manage_all = "workflows_manage_all",
  workflows_manual_run = "workflows_manual_run",

  goals_company_manage = "goals_company_manage",
  goals_department_manage_all = "goals_department_manage_all",
  goals_department_manage_own = "goals_department_manage_own",
  goals_module = "goals_module",
  goals_team_manage_all = "goals_team_manage_all",
  goals_team_manage_own = "goals_team_manage_own",

  tasks_module = "tasks_module",
  tasks_create = "tasks_create",
  tasks_manage_all = "tasks_manage_all",

  clocking_module = "clocking_module",

  ai_assistant_module = "ai_assistant_module",

  esign_manage_templates = "esign_manage_templates",
  esign_download_report = "esign_download_report",
  esign_request = "esign_request",
}

export type PermissionsKeys = keyof typeof ApiPermission;

type PermissionsKeysFields = {
  [key in PermissionsKeys]: PermissionType;
};

export type ApiPermissions = PermissionsKeysFields;

export enum RoleType {
  admin = "admin",
  manager = "manager",
  team_lead = "team_lead",
  all_employees = "all_employees",
  myself = "myself",
  custom = "custom",
}

export enum EmployeeFieldPermission {
  employee_lifecycle_status = "employee_lifecycle_status",
  employee_last_activity_date = "employee_last_activity_date",
  employee_portal_access = "employee_portal_access",

  employee_name = "employee_name",
  employee_name_display = "employee_name_display",
  employee_email = "employee_email",
  employee_date_birth = "employee_date_birth",
  employee_date_birth_display = "employee_date_birth_display",
  employee_gender = "employee_gender",
  employee_hired_on = "employee_hired_on",
  employee_phone_personal = "employee_phone_personal",
  employee_phone = "employee_phone",
  employee_email_personal = "employee_email_personal",
  employee_slack = "employee_slack",
  employee_avatar = "employee_avatar",
  employee_background = "employee_background",

  employee_number = "employee_number",
  employee_address_address_line_1 = "employee_address_address_line_1",
  employee_address_address_line_2 = "employee_address_address_line_2",
  employee_address_country = "employee_address_country",
  employee_address_state = "employee_address_state",
  employee_address_city = "employee_address_city",
  employee_address_zip_code = "employee_address_zip_code",
  employee_name_pronunciation = "employee_name_pronunciation",
  employee_timezone = "employee_timezone",

  manage_employee_position = "manage_employee_position",
  employee_positions_history = "employee_positions_history",
  employee_position_changed_by = "employee_position_changed_by",
  employee_position_job_title = "employee_position_job_title",
  employee_position_job_track = "employee_position_job_track",
  employee_position_job_grade = "employee_position_job_grade",
  employee_position_job_salary = "employee_position_job_salary",
  employee_position_date = "employee_position_date",
  employee_position_reporting_to = "employee_position_reporting_to",
  employee_position_division = "employee_position_division",
  employee_position_department = "employee_position_department",
  employee_position_location = "employee_position_location",
  employee_position_comment = "employee_position_comment",

  manage_employee_employment = "manage_employee_employment",
  employee_employments_history = "employee_employments_history",
  employee_employment_changed_by = "employee_employment_changed_by",
  employee_employment_date = "employee_employment_date",
  employee_employment_legal_entity = "employee_employment_legal_entity",
  employee_employment_employment_type = "employee_employment_employment_type",
  employee_employment_contract = "employee_employment_contract",
  employee_employment_work_pattern = "employee_employment_work_pattern",
  employee_employment_probation = "employee_employment_probation",
  employee_employment_comment = "employee_employment_comment",

  manage_employee_termination = "manage_employee_termination",
  employee_termination_date = "employee_termination_date",
  employee_termination_last_working_date = "employee_termination_last_working_date",
  employee_termination_leave_type = "employee_termination_leave_type",
  employee_termination_reason = "employee_termination_reason",
  employee_termination_replacement_employee = "employee_termination_replacement_employee",
  employee_termination_eligible_for_rehire = "employee_termination_eligible_for_rehire",
  employee_termination_comment = "employee_termination_comment",
  employee_termination_changed_by = "employee_termination_changed_by",

  manage_employee_compensation = "manage_employee_compensation",
  employee_compensations_history = "employee_compensations_history",
  employee_compensation_changed_by = "employee_compensation_changed_by",
  employee_compensation_date = "employee_compensation_date",
  employee_compensation_change_reason = "employee_compensation_change_reason",
  employee_compensation_comment = "employee_compensation_comment",
  employee_compensation_base_salary_gross = "employee_compensation_base_salary_gross",
  employee_compensation_base_salary_net = "employee_compensation_base_salary_net",

  employee_additional_compensation = "employee_additional_compensation",
  employee_compensation_lines = "employee_compensation_lines",
  employee_compensation_total_cost = "employee_compensation_total_cost",
  /** @deprecated The backend keeps this key for compatibility with legacy role data. */
  employee_compensation_off_payroll = "employee_compensation_off_payroll",

  employee_time_off = "employee_time_off",
  employee_goals = "employee_goals",
  employee_recognitions = "employee_recognitions",
  employee_notes = "employee_notes",

  clocking_entries = "clocking_entries",

  see_organization_structure = "see_organization_structure",
  see_departments_structure = "see_departments_structure",
  access_to_leave_timeline = "access_to_leave_timeline",
}

export interface RoleGroup {
  roles: ApiRoleShort[];
  title: string;
  description: string;
  type: RoleGroupType;
}

export enum RoleGroupType {
  base = "base",
  system = "system",
  custom = "custom",
  service_users = "service_users",
}

export type EmployeeFieldPermissionsKeys = keyof typeof EmployeeFieldPermission;

type EmployeeFieldPermissionsKeysFields = {
  [key in EmployeeFieldPermissionsKeys]: PermissionType;
};

export type EmployeeFieldPermissions = EmployeeFieldPermissionsKeysFields;

export interface EmployeeCustomFieldPermissions {
  field_id: number;
  slug: string;
  title: string;
  description: string;
  permission_value: PermissionType;
}

export interface EmployeeCustomTablePermissions {
  slug: string;
  title: string;
  table_id: number;
  description: string;
  history: PermissionType;
  manage_table: PermissionType;
  effective_from: PermissionType;
  changed_by: PermissionType;
  field_permissions: EmployeeCustomFieldPermissions[];
}

export interface EmployeeFolderPermission {
  folder_id: number;
  title: string;
  permission_value: PermissionType;
}

export interface EmployeeWorkflowPermission {
  workflow_id: number;
  title: string;
  run: PermissionType;
  executions: PermissionType;
}

export enum RoleEditTab {
  data = "data",
  feature = "feature",
}

export * from "@/types/rule.types.ts";
